import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Lock as VaultIcon,
  Group as GroupIcon,
  Check as ApproveIcon,
  Close as RejectIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  where,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { familyCodeService } from '../../services/familyCodeService';
import logger from '../../utils/logger';

const AccessControl = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [vaultRequests, setVaultRequests] = useState([]);
  const [groupRequests, setGroupRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'approve', 'reject'

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (currentTab === 0) {
        // Load vault access requests
        const vaultQuery = query(
          collection(db, 'accessRequests'),
          where('type', '==', 'vault'),
          where('status', '==', 'pending')
        );
        const vaultSnapshot = await getDocs(vaultQuery);
        const vaultData = vaultSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVaultRequests(vaultData);
      } else {
        // Load group access requests
        const groupQuery = query(
          collection(db, 'accessRequests'),
          where('type', '==', 'group'),
          where('status', '==', 'pending')
        );
        const groupSnapshot = await getDocs(groupQuery);
        const groupData = groupSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGroupRequests(groupData);
      }
    } catch (err) {
      setError('Failed to load access requests');
      logger.error('Error loading access requests:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTab]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApproveRequest = async (request) => {
    try {
      // Update request status
      await updateDoc(doc(db, 'accessRequests', request.id), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: request.userId
      });

      // If it's a vault request, update user's vault permissions
      if (request.type === 'vault') {
        await updateDoc(doc(db, 'users', request.userId), {
          vaultAccess: {
            ...request.permissions,
            grantedAt: serverTimestamp()
          }
        });
      }

      // If it's a group request, add user to the family-scoped group
      if (request.type === 'group' && request.familyCode) {
        const groupRef = doc(db, 'families', request.familyCode, 'groups', request.groupId);
        await updateDoc(groupRef, {
          members: {
            [request.userId]: {
              joinedAt: serverTimestamp(),
              role: request.role || 'member'
            }
          }
        });
      }

      // If it's a family join request, add user to the family
      if (request.type === 'family' || request.familyCode) {

        // Get user data to pass to joinFamily
        const userDoc = await getDoc(doc(db, 'users', request.userId));
        if (!userDoc.exists()) {
          throw new Error('User not found');
        }

        // User data is available but joinFamily handles the needed fields internally
        // const userData = userDoc.data();

        // Use familyCodeService to properly add user to family
        // This handles: familyCode update, member doc creation, group assignment, memberCount
        await familyCodeService.joinFamily(request.userId, request.familyCode, {
          relationship: request.relationship || 'other',
          autoAssignGroups: ['IMMEDIATE_FAMILY'],
          invitedBy: request.requestedBy || 'admin'
        });

      }

      setSuccess('Request approved successfully');
      loadRequests();
    } catch (err) {
      setError('Failed to approve request');
      logger.error('Error approving request:', err);
    }
  };

  const handleRejectRequest = async (request) => {
    try {
      await updateDoc(doc(db, 'accessRequests', request.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: request.userId
      });
      setSuccess('Request rejected successfully');
      loadRequests();
    } catch (err) {
      setError('Failed to reject request');
      logger.error('Error rejecting request:', err);
    }
  };

  const handleOpenDialog = (type, request) => {
    setDialogType(type);
    setSelectedRequest(request);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRequest(null);
    setDialogType('');
  };

  const renderRequestTable = (requests) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Request Type</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>Requested At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request.id}>
              <TableCell>{request.userEmail}</TableCell>
              <TableCell>
                <Chip
                  label={request.type}
                  color={request.type === 'vault' ? 'primary' : 'secondary'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {request.type === 'vault' ? (
                  <Box>
                    <Typography variant="body2">
                      Access Level: {request.permissions?.level || 'Standard'}
                    </Typography>
                    <Typography variant="body2">
                      Expires: {request.permissions?.expiresAt || 'Never'}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2">
                      Group: {request.groupName}
                    </Typography>
                    <Typography variant="body2">
                      Role: {request.role || 'Member'}
                    </Typography>
                  </Box>
                )}
              </TableCell>
              <TableCell>
                {new Date(request.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Button
                  startIcon={<ApproveIcon />}
                  onClick={() => handleOpenDialog('approve', request)}
                  color="success"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Approve
                </Button>
                <Button
                  startIcon={<RejectIcon />}
                  onClick={() => handleOpenDialog('reject', request)}
                  color="error"
                  size="small"
                >
                  Reject
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          centered
        >
          <Tab
            icon={<VaultIcon />}
            label="Vault Access Requests"
            iconPosition="start"
          />
          <Tab
            icon={<GroupIcon />}
            label="Group Access Requests"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {currentTab === 0 ? renderRequestTable(vaultRequests) : renderRequestTable(groupRequests)}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {dialogType === 'approve' ? 'Approve Request' : 'Reject Request'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {dialogType} this {selectedRequest?.type} access request from {selectedRequest?.userEmail}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={() => {
              if (dialogType === 'approve') handleApproveRequest(selectedRequest);
              else handleRejectRequest(selectedRequest);
            }}
            variant="contained"
            color={dialogType === 'approve' ? 'success' : 'error'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessControl; 